import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { firstValueFrom } from 'rxjs';
import { MaskForGuestPipe } from '@luzula/luzula-lib';
import { DataService } from 'src/app/data.service';
import { dateString, punktartBeschreibung } from '../../data-model';
import { verfahrenVon } from '../verfahren/verfahren';
import { AbschnittWahlComponent } from './abschnitt-wahl.component';
import { AbschnittId, hatAbschnittInhalt } from './punkt-abschnitte';
import { Quellpunkt, abstandText, sucheQuellpunkte } from './quellpunkte';

/** Was das Fenster beim Übernehmen zurückgibt. */
export interface PunktdatenHolenErgebnis {
	quelle: any;
	abschnitte: AbschnittId[];
}

/** Höchstens so viele Punkte stehen in der Liste; die Suche findet den Rest. */
export const QUELLPUNKTE_HOECHSTENS = 100;

/**
 * Weg 2 aus PLAN-punktdaten-uebernehmen.md: in ein vorhandenes Protokoll Daten aus einem
 * anderen Punkt holen.
 *
 * Erst den Quellpunkt wählen (nur das aktive Projekt, die nächsten zuerst), dann die
 * Abschnitte. Hat der Zielpunkt in einem gewählten Abschnitt schon Angaben, steht
 * „wird ersetzt" daneben, und der Knopf sagt, wie viele Abschnitte ersetzt werden
 * (Nutzer-Entscheidung 28.09.2026: erst die Vorschau, dann ersetzen).
 *
 * Das Fenster schreibt nichts. Es gibt Quelle und Wahl zurück; die Reiter-Ansicht
 * übernimmt und setzt „geändert" - gespeichert wird wie jede andere Bearbeitung.
 */
@Component({
	selector: 'bp-punktdaten-holen',
	standalone: true,
	imports: [CommonModule, FormsModule, AbschnittWahlComponent, MaskForGuestPipe],
	template: `
		<div class="modal-header">
			<h4 class="modal-title">Aus anderem Punkt übernehmen</h4>
			<button type="button" class="btn-close" aria-label="Schließen" (click)="activeModal.dismiss()"></button>
		</div>
		<div class="modal-body">
			@if (!quelle) {
				<p class="small text-body-secondary mb-2">Punkte dieses Projekts, die nächsten zuerst.</p>
				<input type="search" class="form-control mb-2" name="suche" placeholder="Nummer suchen"
					autocomplete="off" [(ngModel)]="suche">
				@if (fehler) {
					<div class="alert alert-warning py-2">{{ fehler }}</div>
				}
				@if (kandidaten.length === 0) {
					<div class="text-body-secondary">In diesem Projekt gibt es keinen anderen Punkt mit Protokoll.</div>
				} @else if (gefiltert().length === 0) {
					<div class="text-body-secondary">Kein Punkt mit dieser Nummer.</div>
				}
				<div class="list-group">
					@for (q of gefiltert(); track q.id) {
						<button type="button" class="list-group-item list-group-item-action quell-zeile"
							[disabled]="laedt" (click)="waehle(q)">
							<span class="quell-nummer">{{ q.nummer || 'ohne Nummer' }}</span>
							<span class="quell-art">{{ punktartName(q.punktart) }}</span>
							<span class="quell-abstand">{{ abstandText(q.abstandM) }}</span>
							<span class="quell-datum">{{ datumText(q.datum) | maskForGuest }}</span>
						</button>
					}
				</div>
				@if (weitere() > 0) {
					<div class="small text-body-secondary mt-2">{{ weitereText() }}</div>
				}
			} @else {
				<div class="quell-gewaehlt mb-3">
					<button type="button" class="btn btn-sm btn-outline-secondary" title="Anderen Punkt wählen" (click)="zurueck()">
						<i class="bi bi-arrow-left"></i>
					</button>
					<span>{{ quellTitel() }}</span>
				</div>
				<bp-abschnitt-wahl [quelle]="quelle" [ziel]="ziel" [punktart]="zielPunktart()" [(gewaehlt)]="gewaehlt"></bp-abschnitt-wahl>
				<p class="small text-body-secondary mt-3 mb-0">
					Danach ist der Punkt geändert, aber noch nicht gespeichert. Prüfen, dann speichern oder verwerfen.
				</p>
			}
		</div>
		<div class="modal-footer">
			<button type="button" class="btn btn-outline-secondary" (click)="activeModal.dismiss()">Abbrechen</button>
			@if (quelle) {
				<button type="button" class="btn"
					[class.btn-warning]="ersetzteAnzahl() > 0"
					[class.btn-primary]="ersetzteAnzahl() === 0"
					[disabled]="gewaehlt.length === 0"
					(click)="uebernehmen()">{{ knopfText() }}</button>
			}
		</div>
	`,
	styles: [`
		.quell-zeile { display: grid; grid-template-columns: minmax(4rem, auto) 1fr auto auto; gap: 0.75rem; align-items: center; text-align: left; }
		.quell-nummer { font-weight: 600; }
		.quell-art { color: var(--bs-secondary-color); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
		.quell-abstand, .quell-datum { font-size: 0.85rem; white-space: nowrap; color: var(--bs-secondary-color); }
		.quell-gewaehlt { display: flex; align-items: center; gap: 0.5rem; font-weight: 600; }
	`],
})
export class PunktdatenHolenComponent {
	activeModal = inject(NgbActiveModal);
	private ds = inject(DataService);

	/** Der Punkt, in den übernommen wird (das offene Protokoll). */
	ziel: any;
	/** Die möglichen Quellpunkte, schon gefiltert und sortiert (`quellpunkteFuer`). */
	kandidaten: Quellpunkt[] = [];

	suche = '';
	quelle: any = null;
	quellpunkt: Quellpunkt | null = null;
	gewaehlt: AbschnittId[] = [];
	laedt = false;
	fehler = '';

	abstandText = abstandText;

	gefiltert(): Quellpunkt[] {
		return sucheQuellpunkte(this.kandidaten, this.suche).slice(0, QUELLPUNKTE_HOECHSTENS);
	}

	weitere(): number {
		return Math.max(0, sucheQuellpunkte(this.kandidaten, this.suche).length - QUELLPUNKTE_HOECHSTENS);
	}

	weitereText(): string {
		return `und ${this.weitere()} weitere - über die Nummer suchen`;
	}

	punktartName(punktart: string): string {
		return punktartBeschreibung(punktart);
	}

	datumText(iso: string | undefined): string {
		return iso ? dateString(iso) : '';
	}

	zielPunktart(): string {
		return this.ziel?.type ?? '';
	}

	quellTitel(): string {
		const q = this.quellpunkt;
		if (!q) return '';
		const teile = [`Punkt ${q.nummer || 'ohne Nummer'}`, this.punktartName(q.punktart)];
		const abstand = abstandText(q.abstandM);
		if (abstand) teile.push(`${abstand} entfernt`);
		return teile.join(', ');
	}

	/**
	 * Lädt den ganzen Datensatz des gewählten Punktes. Erst hier steht das Verfahren fest
	 * (die Punktliste führt es nicht): Aus einer KA-Aufnahme lässt sich nichts in ein
	 * SEA-Protokoll holen.
	 */
	async waehle(q: Quellpunkt): Promise<void> {
		this.fehler = '';
		this.laedt = true;
		try {
			const doc = await firstValueFrom(this.ds.getDocument$(q.id));
			const punkt = this.ds.updateBohrpunkt(doc, false);
			if (verfahrenVon(punkt) !== 'sea95') {
				this.fehler = 'Dieser Punkt ist nach der Bodenkundlichen Kartieranleitung aufgenommen. Daraus lässt sich hier nichts übernehmen.';
				return;
			}
			this.quelle = punkt;
			this.quellpunkt = q;
			this.gewaehlt = [];
		} catch {
			this.fehler = 'Der Punkt ließ sich nicht laden.';
		} finally {
			this.laedt = false;
		}
	}

	zurueck(): void {
		this.quelle = null;
		this.quellpunkt = null;
		this.gewaehlt = [];
	}

	/** Wie viele der gewählten Abschnitte im Zielpunkt schon Angaben haben. */
	ersetzteAnzahl(): number {
		return this.gewaehlt.filter(id => hatAbschnittInhalt(this.ziel, id)).length;
	}

	knopfText(): string {
		const n = this.ersetzteAnzahl();
		if (n === 0) return 'Übernehmen';
		return n === 1 ? 'Übernehmen und 1 Abschnitt ersetzen' : `Übernehmen und ${n} Abschnitte ersetzen`;
	}

	uebernehmen(): void {
		if (!this.quelle || this.gewaehlt.length === 0) return;
		const ergebnis: PunktdatenHolenErgebnis = { quelle: this.quelle, abschnitte: [...this.gewaehlt] };
		this.activeModal.close(ergebnis);
	}
}
